import { ServiceRequestTypeEnum } from '@enums/service-request-type.enum';

import { ServiceRequestWizardStepId } from '../enums/service-request-wizard-step-id.enum';
import { ServiceRequestWizardContext } from '../interfaces/service-request-wizard-context.interface';
import { ServiceRequestWizardStepConfig } from '../interfaces/service-request-wizard-step-config.interface';

/**
 * Ordered create-service-request wizard steps. Reorder by moving entries in this array.
 */
export const SERVICE_REQUEST_WIZARD_STEPS: readonly ServiceRequestWizardStepConfig[] = [
  {
    id: ServiceRequestWizardStepId.CUSTOMER_INFO,
    route: 'customer-info',
    title: 'Customer Info',
    loadChildren: () => import('../modules/customer-info/customer-info.module').then((m) => m.CreateServiceRequestCustomerInfoModule),
  },
  {
    id: ServiceRequestWizardStepId.SAVED_TEMPLATE,
    route: 'saved-template',
    title: 'Proceed with a Saved Template?',
    isVisible: (context: ServiceRequestWizardContext): boolean => context.hasSavedTemplates,
    loadChildren: () => import('../modules/saved-template/saved-template.module').then((m) => m.CreateServiceRequestSavedTemplateModule),
  },
  {
    id: ServiceRequestWizardStepId.SERVICE_TYPE,
    route: 'service-type',
    title: 'Service Type / Equipment',
    loadChildren: () => import('../modules/service-type/service-type.module').then((m) => m.CreateServiceRequestServiceTypeModule),
  },
  {
    id: ServiceRequestWizardStepId.ORIGIN_LOCATION,
    route: 'origin-location',
    title: 'Origin Location',
    loadChildren: () => import('../modules/origin-location/origin-location.module').then((m) => m.CreateServiceRequestOriginLocationModule),
  },
  {
    id: ServiceRequestWizardStepId.DESTINATION,
    route: 'destination',
    title: 'Destination',
    isVisible: (context: ServiceRequestWizardContext): boolean => context.serviceType !== ServiceRequestTypeEnum.SPECIMEN,
    loadChildren: () => import('../modules/destination/destination.module').then((m) => m.CreateServiceRequestDestinationModule),
  },
  {
    id: ServiceRequestWizardStepId.PICKUP_DROPOFF_TIME,
    route: 'pickup-dropoff-time',
    title: 'Pickup / Drop off Time',
    loadChildren: () =>
      import('../modules/pickup-dropoff-time/pickup-dropoff-time.module').then((m) => m.CreateServiceRequestPickupDropoffTimeModule),
  },
  {
    id: ServiceRequestWizardStepId.ADDITIONAL_INFO,
    route: 'additional-info',
    title: 'Additional Info',
    loadChildren: () => import('../modules/additional-info/additional-info.module').then((m) => m.CreateServiceRequestAdditionalInfoModule),
  },
  {
    id: ServiceRequestWizardStepId.ID_INFORMATION,
    route: 'id-information',
    title: 'Enter ID Information',
    loadChildren: () => import('../modules/id-information/id-information.module').then((m) => m.CreateServiceRequestIdInformationModule),
  },
  {
    id: ServiceRequestWizardStepId.TEAM_MEMBERS,
    route: 'team-members',
    title: 'Add Team Members',
    isVisible: (context: ServiceRequestWizardContext): boolean => context.serviceType === ServiceRequestTypeEnum.TEAM,
    loadChildren: () => import('../modules/team-members/team-members.module').then((m) => m.CreateServiceRequestTeamMembersModule),
  },
  {
    id: ServiceRequestWizardStepId.DROP_OFF_LOCATIONS,
    route: 'drop-off-locations',
    title: 'Drop Off Locations',
    isVisible: (context: ServiceRequestWizardContext): boolean => context.serviceType === ServiceRequestTypeEnum.SPECIMEN,
    loadChildren: () =>
      import('../modules/drop-off-locations/drop-off-locations.module').then((m) => m.CreateServiceRequestDropOffLocationsModule),
  },
  {
    id: ServiceRequestWizardStepId.SPECIAL_INSTRUCTIONS,
    route: 'special-instructions',
    title: 'Special Instructions',
    isVisible: (context: ServiceRequestWizardContext): boolean => context.serviceType !== ServiceRequestTypeEnum.SPECIMEN,
    loadChildren: () =>
      import('../modules/special-instructions/special-instructions.module').then((m) => m.CreateServiceRequestSpecialInstructionsModule),
  },
  {
    id: ServiceRequestWizardStepId.NOTES_AND_FILES,
    route: 'notes-and-files',
    title: 'Notes and Files',
    loadChildren: () => import('../modules/notes-and-files/notes-and-files.module').then((m) => m.CreateServiceRequestNotesAndFilesModule),
  },
  {
    id: ServiceRequestWizardStepId.PREVIEW,
    route: 'preview',
    title: 'Review and Submit',
    loadChildren: () =>
      import('../modules/create-service-request-preview/create-service-request-preview.module').then(
        (m) => m.CreateServiceRequestPreviewModule,
      ),
  },
];

/** Route path of the first wizard step (entry point). */
export const CREATE_SERVICE_REQUEST_FIRST_STEP_ROUTE: string = SERVICE_REQUEST_WIZARD_STEPS[0].route;
